'use client';

import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { ChartHamburgerMenu } from './combined/ChartHamburgerMenu';
import { ChartSkeleton } from './ChartSkeleton';
import { Breadcrumbs } from '@/components/ui-components/Breadcrumbs/Breadcrumbs';
import { theme } from '../../config/theme';

const LayeredDonutChartContainer = styled.div`
  background: ${theme.colors.white};
  border-radius: 8px;
  transition: all 0.3s ease;
  width: 100%;
  overflow: visible;
`;

const RightSectionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ChartContent = styled.div`
  padding: 0 24px 24px;
  min-height: 400px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  overflow: visible;
  border-radius: 8px;
`;

const ChartWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 400px;
`;

const LabelsContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
`;

const LabelItem = styled.div<{ $left: number; $top: number; $color: string }>`
  position: absolute;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top}px;
  transform: translateY(-50%);
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const TooltipWrapper = styled.div<{ $left: number; $top: number; $visible: boolean }>`
  position: absolute;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top}px;
  transform: translateY(-50%);
  z-index: 1000;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
`;

const LabelText = styled.span`
  font-size: 14px;
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.gray[900]};
  white-space: nowrap;
  background: ${theme.colors.white};
  padding: 2px 4px;
`;

const ColorIndicator = styled.div<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const ConnectingLine = styled.svg<{ $x1: number; $y1: number; $x2: number; $y2: number; $color: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: -1;
`;

const TooltipContainer = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.gray[300]};
  border-radius: 8px;
  padding: 16px;
  box-shadow: ${theme.shadows.md};
  min-width: 200px;
  z-index: 1000;
  position: relative;
`;

const TooltipTitle = styled.div`
  font-size: 14px;
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.gray[900]};
  margin-bottom: 12px;
`;

const TooltipGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  text-align: center;
`;

const TooltipColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TooltipValue = styled.div<{ $color?: string }>`
  font-size: 18px;
  font-weight: ${theme.fontWeights.bold};
  color: ${({ $color }) => $color || theme.colors.gray[900]};
`;

const TooltipLabel = styled.div`
  font-size: 11px;
  color: ${theme.colors.gray[600]};
  font-weight: ${theme.fontWeights.medium};
`;

export interface LayeredDonutElement {
  element: string;
  totalChecks: number;
  present: number;
  successRate: number; // 0-100
}

export interface LayeredDonutChartRechartsProps {
  data: LayeredDonutElement[];
  height?: number;
  loading?: boolean;
  className?: string;
  isFullScreen?: boolean;
}

// Color palette for elements - each element gets a unique color
const ELEMENT_COLORS = [
  '#FF383C', // red
  '#FF8D28', // orange
  '#FFCC00', // yellow/amber
  '#34C759', // green
  '#00C8B3', // cyan/light blue
  '#0088FF', // blue
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#10B981', // emerald
  '#F97316', // orange-500
];

// Get unique color for each element by index
const getElementColor = (index: number): string => {
  return ELEMENT_COLORS[index % ELEMENT_COLORS?.length];
};

// Get color based on success rate percentage
const getSuccessRateColor = (successRate: number): string => {
  if (successRate >= 80) {
    return '#10b981'; // Green - good
  } else if (successRate >= 50) {
    return '#f59e0b'; // Yellow - warning
  } else {
    return '#ef4444'; // Red - critical
  }
};

export const LayeredDonutChartRecharts: React.FC<LayeredDonutChartRechartsProps> = ({
  data,
  height = 400,
  loading = false,
  className,
  isFullScreen = false // Optional prop, but we'll use internal state
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const [zoomEnabled, setZoomEnabled] = useState(false);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Adjust height based on fullscreen state
  const chartHeight = isFullscreen ? 600 : height;

  // Monitor fullscreen changes for THIS specific chart only
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (!chartRef.current) return;
      
      const doc = document as Document & {
        webkitFullscreenElement?: Element;
        mozFullScreenElement?: Element;
        msFullscreenElement?: Element;
      };
      
      const fullscreenElement = 
        document.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement;
      
      // Only update if THIS chart is the one in fullscreen (or exiting fullscreen)
      const isThisChartFullscreen = fullscreenElement === chartRef.current;
      const wasThisChartFullscreen = isFullscreen;
      
      if (isThisChartFullscreen !== wasThisChartFullscreen) {
        setIsFullscreen(isThisChartFullscreen);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [isFullscreen]);

  // Sort data by success rate (descending) for better visualization
  const sortedData = React.useMemo(() => {
    return [...data].sort((a, b) => b.successRate - a.successRate);
  }, [data]);

  // Measure container size for label positioning
  React.useEffect(() => {
    const updateSize = () => {
      if (chartWrapperRef.current) {
        const rect = chartWrapperRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Show skeleton if loading
  if (loading) {
    const skeletonHeight = isFullscreen ? 600 : height;
    return <ChartSkeleton type="pie" height={`${skeletonHeight}px`} showLegend />;
  }

  // Create table data for hamburger menu
  const tableData: Array<Record<string, string | number>> = sortedData.map((element) => ({
    element: element.element,
    totalChecks: element.totalChecks,
    present: element.present,
    successRate: `${element.successRate.toFixed(1)}%`
  }));

  // Calculate radius for each ring
  const numElements = sortedData?.length;
  
  // Default values - double the size in full screen mode
  const DEFAULT_BASE_OUTER_RADIUS = isFullscreen ? 300 : 150;
  const DEFAULT_RING_WIDTH = isFullscreen ? 24 : 12;
  const DEFAULT_GAP = isFullscreen ? 12 : 6;
  const MIN_INNER_RADIUS = isFullscreen ? 40 : 20; // Minimum inner radius for the innermost ring
  
  // Calculate total space needed for all rings
  // For n rings: baseOuterRadius - (n * ringWidth + (n-1) * gap) >= MIN_INNER_RADIUS
  // So: baseOuterRadius >= n * ringWidth + (n-1) * gap + MIN_INNER_RADIUS
  const totalRingSpace = numElements * DEFAULT_RING_WIDTH + (numElements - 1) * DEFAULT_GAP;
  const requiredBaseRadius = totalRingSpace + MIN_INNER_RADIUS;
  
  let baseOuterRadius: number;
  let ringWidth: number;
  let gap: number;
  
  if (requiredBaseRadius > DEFAULT_BASE_OUTER_RADIUS) {
    // Need to scale down ringWidth and gap to fit within default base radius
    const availableSpace = DEFAULT_BASE_OUTER_RADIUS - MIN_INNER_RADIUS;
    const scaleFactor = availableSpace / totalRingSpace;
    baseOuterRadius = DEFAULT_BASE_OUTER_RADIUS;
    ringWidth = DEFAULT_RING_WIDTH * scaleFactor;
    gap = DEFAULT_GAP * scaleFactor;
  } else {
    // Use default values if there's enough space
    baseOuterRadius = DEFAULT_BASE_OUTER_RADIUS;
    ringWidth = DEFAULT_RING_WIDTH;
    gap = DEFAULT_GAP;
  }
  
  // Chart center and dimensions for label positioning
  const startAngle = 450; // Start from bottom (270 + 180 = 450)
  
  // Helper function to convert angle to coordinates
  const getAngleCoordinates = (angleDeg: number, radius: number, centerX: number, centerY: number) => {
    // Convert angle to radians (Recharts uses 0° at top, so we adjust)
    // startAngle 450 means starting from bottom (270° + 180°)
    const angleRad = ((angleDeg - 90) % 360) * (Math.PI / 180);
    const x = centerX + radius * Math.cos(angleRad);
    const y = centerY + radius * Math.sin(angleRad);
    return { x, y };
  };



  const chartTitle = 'Functional Element Checks';

  return (
    <LayeredDonutChartContainer ref={chartRef} className={className}>
      <Breadcrumbs 
        items={[{ label: chartTitle }]} 
        style={{ borderRadius: '8px'}}
        rightSection={
          <RightSectionWrapper>
            <ChartHamburgerMenu 
              chartTitle={chartTitle}
              tableData={tableData}
              chartContainerRef={chartRef}
              onZoomToggle={setZoomEnabled}
              initialZoomEnabled={zoomEnabled}
            />
          </RightSectionWrapper>
        }
      />
      <ChartContent>
        {sortedData?.length > 0 ? (
          <>
            <ChartWrapper ref={chartWrapperRef}>
              <ResponsiveContainer width="100%" height={chartHeight}>
                <PieChart>
                  {sortedData.map((element, index) => {
                    // Calculate radii for this ring (outer rings have larger radii)
                    const outerRadius = baseOuterRadius - (index * (ringWidth + gap));
                    const innerRadius = outerRadius - ringWidth;
                    const color = getElementColor(index);

                    // Convert hex color to rgba with 0.8 opacity
                    const hexToRgba = (hex: string, opacity: number): string => {
                      const r = parseInt(hex.slice(1, 3), 16);
                      const g = parseInt(hex.slice(3, 5), 16);
                      const b = parseInt(hex.slice(5, 7), 16);
                      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
                    };
                    const colorWithOpacity = hexToRgba(color, 0.2);

                    // Create data for this ring: success and failure portions
                    const ringData = [
                      { 
                        name: element.element, 
                        value: element.successRate,
                        color: color,
                        totalChecks: element.totalChecks,
                        present: element.present
                      },
                      { 
                        name: 'absent', 
                        value: 100 - element.successRate,
                        color: colorWithOpacity,
                        totalChecks: element.totalChecks,
                        present: element.present
                      }
                    ];

                    return (
                      <Pie
                        key={`ring-${index}`}
                        data={ringData}
                        cx="65%"
                        cy="50%"
                        startAngle={450} // Start from bottom (270 + 180 = 450)
                        endAngle={90} // Complete full circle clockwise
                        innerRadius={innerRadius}
                        outerRadius={outerRadius}
                        dataKey="value"
                        stroke="#ffffff"
                        strokeWidth={2}
                        cornerRadius={3}
                        nameKey="name"
                      >
                        {ringData.map((entry, cellIndex) => (
                          <Cell 
                            key={`cell-${index}-${cellIndex}`} 
                            fill={entry.color}
                          />
                        ))}
                      </Pie>
                    );
                  })}
                </PieChart>
              </ResponsiveContainer>
              {containerSize && (() => {
                // Chart center is at 60% of width, 50% of height
                const centerX = containerSize.width * 0.6;
                const centerY = containerSize.height / 2;
                
                // Calculate label positions - vertical stack from top with 15px gap
                const labelStartY = 20; // Start from top with some margin
                const labelGap = 15; // Gap between labels
                const labelHeight = 20; // Approximate height of each label
                const labelX = 20; // Fixed X position on the left
                
                  // Calculate label positions based on vertical stack
                const labelPositions = sortedData.map((element, index) => {
                  const outerRadius = baseOuterRadius - (index * (ringWidth + gap));
                  // Convert radius to actual pixels - ResponsiveContainer uses percentage-based sizing
                  // The chart is at 60% cx, so we need to calculate based on the actual rendered size
                  const chartWidth = containerSize.width;
                  const chartHeight = containerSize.height;
                  const minSize = Math.min(chartWidth, chartHeight);
                  
                  // Calculate the actual radius in pixels
                  // ResponsiveContainer scales based on the container, so we need to match that
                  // Assuming the chart takes about 40% of the container width/height
                  const chartScale = minSize * 0.4; // Approximate chart size
                  const actualOuterRadius = (outerRadius / 150) * (chartScale * 0.5); // Scale from base 150
                  
                  // Get coordinates for the start of this ring segment (for connecting line)
                  // The line should point to the outer edge of the ring at the start angle
                  const segmentCoords = getAngleCoordinates(startAngle, actualOuterRadius, centerX, centerY);
                  
                  // Position label vertically stacked: start position + (index * (height + gap))
                  const labelY = labelStartY + (index * (labelHeight + labelGap));
                  
                  // Line coordinates: from label to pie edge
                  // Start from the color indicator (labelX + 3px radius + 4px gap = labelX + 7px)
                  const indicatorEndX = labelX + 7; // 3px radius + 4px gap
                  const lineStartX = indicatorEndX;
                  const lineStartY = labelY;
                  // End at the pie segment outer edge
                  const lineEndX = segmentCoords.x;
                  const lineEndY = segmentCoords.y;
                  
                  return {
                    element,
                    index,
                    labelX,
                    labelY,
                    lineStartX,
                    lineStartY,
                    lineEndX,
                    lineEndY,
                    color: getElementColor(index),
                  };
                });

                return (
                  <LabelsContainer>
                    {/* <svg width={containerSize.width} height={containerSize.height} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
                      {labelPositions.map(({ lineStartX, lineStartY, lineEndX, lineEndY, color, index }) => (
                        <line
                          key={`line-${index}`}
                          x1={lineStartX}
                          y1={lineStartY}
                          x2={lineEndX}
                          y2={lineEndY}
                          stroke={color}
                          strokeWidth="1"
                          opacity="1"
                        />
                      ))}
                    </svg> */}
                    {labelPositions.map(({ element, index, labelX, labelY, color }) => (
                      <React.Fragment key={`label-${index}`}>
                        <LabelItem
                          $left={labelX}
                          $top={labelY}
                          $color={color}
                          onMouseEnter={() => setHoveredIndex(index)}
                          onMouseLeave={() => setHoveredIndex(null)}
                        >
                          <ColorIndicator $color={color} />
                          <LabelText>
                            {element.element} ({element.successRate.toFixed(0)}%)
                          </LabelText>
                        </LabelItem>
                        <TooltipWrapper
                          $left={labelX + (isFullscreen?200:-290)}
                          $top={labelY + (isFullscreen?0:50)}
                          $visible={hoveredIndex === index}
                        >
                          <TooltipContainer>
                            <TooltipTitle>{element.element}</TooltipTitle>
                            <TooltipGrid>
                              <TooltipColumn>
                                <TooltipValue $color={theme.colors.gray[900]}>{element.totalChecks}</TooltipValue>
                                <TooltipLabel>Total Checks</TooltipLabel>
                              </TooltipColumn>
                              <TooltipColumn>
                                <TooltipValue $color={getSuccessRateColor(element.successRate)}>{element.present}</TooltipValue>
                                <TooltipLabel>Present</TooltipLabel>
                              </TooltipColumn>
                              <TooltipColumn>
                                <TooltipValue $color={getSuccessRateColor(element.successRate)}>{element.successRate.toFixed(0)}%</TooltipValue>
                                <TooltipLabel>Success Rate</TooltipLabel>
                              </TooltipColumn>
                            </TooltipGrid>
                          </TooltipContainer>
                        </TooltipWrapper>
                      </React.Fragment>
                    ))}
                  </LabelsContainer>
                );
              })()}
            </ChartWrapper>
          </>
        ) : (
          <div style={{ textAlign: 'center', color: theme.colors.gray[500], width: '100%' }}>
            No data available
          </div>
        )}
      </ChartContent>
    </LayeredDonutChartContainer>
  );
};